/**
 * O grafo de cena dos jogadores, sincronizado com o estado AUTORITATIVO.
 *
 * Invariante 2: o que chega aqui é o que o servidor disse. Este arquivo NEVER
 * inventa posição, NEVER decide acerto e NEVER simula nada — na fase 01 a predição
 * entra ANTES daqui, corrigindo o que o servidor confirmar.
 *
 * Alocação acontece quando jogador entra, NEVER por frame: malha e material são
 * reaproveitados, e o material é compartilhado entre jogadores do mesmo papel.
 */
import { tokens } from '@game/design';
import { balance } from '@game/gamedata';
import {
  BoxGeometry,
  Color,
  Mesh,
  MeshLambertMaterial,
  Vector2,
  type BufferGeometry,
  type Object3D,
  type Scene,
} from 'three';
import type { AvatarInstance, AvatarRig } from './avatar-rig.js';

/**
 * Só o que o render precisa saber de um jogador. Estrutural de propósito: o
 * `snapshot.players` do protocolo encaixa direto, e `render/` NEVER importa `net/`.
 */
export interface PlayerView {
  readonly id: number;
  readonly x: number;
  readonly z: number;
  readonly hp: number;
  /**
   * Para onde o jogador aponta o campo. Chega pelo fio desde o `PROTOCOL_VERSION` 3,
   * e continua OPCIONAL aqui de propósito: ausente é "NEVER gire este avatar", que é
   * o comportamento do cubo e o de um cliente que ainda não sabe a mira de alguém.
   * Inventar uma a partir da velocidade seria o render adivinhando estado do
   * servidor — exatamente o que o invariante 2 proíbe.
   */
  readonly facingX?: number;
  readonly facingZ?: number;
}

/**
 * Quanto do valor NOVO entra na velocidade a cada frame.
 *
 * A velocidade que escolhe a animação é DERIVADA da posição autoritativa, e por
 * isso ela treme: interpolação entrega salto pequeno em frame curto. Sem
 * suavizar, o robô pisca entre parado e andando na beirada do limiar. 0,2 é
 * lento o bastante para NEVER piscar e rápido o bastante para a partida do lugar
 * NEVER parecer atrasada.
 */
const SPEED_SMOOTHING = 0.2;

export class World {
  private readonly meshes = new Map<number, Mesh>();
  /** Só existe com a flag `robotAnim`: um esqueleto e um mixer POR jogador. */
  private readonly rigs = new Map<number, AvatarInstance>();
  private rig: AvatarRig | undefined;
  private readonly lastPosition = new Map<number, Vector2>();
  private readonly speeds = new Map<number, number>();
  private readonly boxGeometry: BoxGeometry;
  /** A que está em uso. Começa no cubo e NEVER volta depois de trocada. */
  private geometry: BufferGeometry;
  /**
   * Quanto o centro da malha sobe para ela apoiar no chão. O cubo é centrado na
   * própria origem, então sobe meio lado; a malha do avatar já vem com a base em
   * `y = 0` (`avatar.ts`), então NEVER sobe. É este número que diverge entre as
   * duas geometrias, e é por isso que ele mora ao lado de quem trocou.
   */
  private groundOffset: number;
  private avatarGeometry: BufferGeometry | undefined;
  private readonly selfMaterial: MeshLambertMaterial;
  private readonly otherMaterial: MeshLambertMaterial;
  private readonly deadMaterial: MeshLambertMaterial;

  /** `selfId` é o `playerId` do `welcome`: quem é você tem cor própria. */
  constructor(
    private readonly scene: Scene,
    private selfId: number,
  ) {
    const side = balance.player.radius * 2;
    this.boxGeometry = new BoxGeometry(side, side, side);
    this.geometry = this.boxGeometry;
    this.groundOffset = balance.player.radius;
    // `flatShading` nos três: a malha de runtime vem SEM normais (ADR-0016), e
    // com ele o three as deriva no fragmento. É também o que evita o "cinza
    // plástico" da lista de proibições — facetado lê como escolha, e o degradê
    // suave de uma malha decimada lê como malha decimada.
    this.selfMaterial = new MeshLambertMaterial({
      color: new Color(tokens.color.role.accentText),
      flatShading: true,
    });
    this.otherMaterial = new MeshLambertMaterial({
      color: new Color(tokens.color.role.textSecondary),
      flatShading: true,
    });
    // `semantic`, não `role`: os papéis de `@game/design` descrevem superfície e
    // texto de UI, e não existe papel para "jogador caiu" no mundo 3D. Quando a
    // fase 03 definir a arte, isto vira um papel próprio.
    this.deadMaterial = new MeshLambertMaterial({
      color: new Color(tokens.color.semantic.danger),
      flatShading: true,
    });
  }

  /**
   * Troca o cubo pela malha do avatar. Chamado UMA vez, quando o `.glb` termina
   * de chegar — e chegar tarde é o caso normal, NEVER o excepcional: o jogo já
   * está rodando quando isso acontece, então a troca precisa alcançar quem já
   * está na cena, e não só quem entrar depois.
   *
   * NEVER solta o cubo: em aparelho que perdeu o asset no meio (rede móvel cai),
   * o cubo é o fallback, e soltar a geometria dele seria trocar avatar por
   * tela preta.
   */
  useAvatarGeometry(geometry: BufferGeometry): void {
    this.avatarGeometry = geometry;
    this.geometry = geometry;
    this.groundOffset = 0;
    for (const mesh of this.meshes.values()) mesh.geometry = geometry;
  }

  /**
   * Troca o cubo pelo avatar ANIMADO. Como `useAvatarGeometry`, chega tarde e
   * precisa alcançar quem já está na sala.
   *
   * Exclusivo com o avatar estático de propósito: as duas flags ligadas juntas
   * seriam dois robôs no mesmo lugar, e quem ganha é quem chegou por último.
   * `main.ts` liga UMA.
   */
  useAvatarRig(rig: AvatarRig): void {
    this.rig = rig;
    this.groundOffset = 0;
    for (const [id, mesh] of this.meshes) {
      this.scene.remove(mesh);
      this.meshes.delete(id);
      this.addRig(id, mesh.position.x, mesh.position.z);
    }
  }

  /**
   * Avança as animações e escolhe qual delas toca.
   *
   * Separado do `sync` porque depende de TEMPO, e `sync` depende de ESTADO: o
   * mesmo snapshot pode ser desenhado duas vezes no mesmo frame (chegada e
   * frame), e animar duas vezes correria o passo ao dobro.
   *
   * A velocidade sai da diferença de posição AUTORITATIVA, e isto NEVER fere o
   * invariante 2: nada aqui vira estado, e ninguém pergunta a este número o que
   * aconteceu no jogo. Ele decide só qual clipe toca na tela.
   */
  animate(deltaMs: number): void {
    if (this.rigs.size === 0) return;
    const seconds = deltaMs / 1000;
    for (const [id, instance] of this.rigs) {
      const previous = this.lastPosition.get(id);
      const here = instance.object.position;
      let speed = this.speeds.get(id) ?? 0;
      if (previous !== undefined && seconds > 0) {
        const moved = Math.hypot(here.x - previous.x, here.z - previous.y) / seconds;
        speed += (moved - speed) * SPEED_SMOOTHING;
        this.speeds.set(id, speed);
      }
      // O `Vector2` é o mesmo desde que o jogador entrou (`addRig`): escrever nele
      // é o que mantém este laço sem alocação (PERFORMANCE M5).
      previous?.set(here.x, here.z);
      instance.update(seconds, speed);
    }
  }

  get trackedIds(): number[] {
    return [...this.meshes.keys()];
  }

  meshFor(id: number): Mesh | undefined {
    return this.meshes.get(id);
  }

  /**
   * Traz o grafo para o estado que o servidor descreveu: cria quem entrou,
   * remove quem saiu, move o resto.
   *
   * A varredura é O(n²) e isso é de propósito: `balance.room.maxPlayers` cabe numa
   * mão, e um laço sobre array pequeno NEVER aloca — um `Set` por snapshot alocaria
   * a cada snapshot.
   */
  sync(players: readonly PlayerView[]): void {
    for (const player of players) {
      const instance = this.rig === undefined ? undefined : this.rigOf(player.id);
      let mesh: Mesh | Object3D | undefined = instance?.object;
      if (instance !== undefined) {
        instance.setMaterial(this.materialFor(player));
      } else {
        let box = this.meshes.get(player.id);
        if (box === undefined) {
          box = new Mesh(this.geometry, this.materialFor(player));
          this.meshes.set(player.id, box);
          this.scene.add(box);
        } else {
          box.material = this.materialFor(player);
        }
        mesh = box;
      }
      if (mesh === undefined) continue;
      mesh.position.set(player.x, this.groundOffset, player.z);
      // `atan2` de (x, z) e NEVER de (z, x): o ângulo de rotação em Y do three
      // cresce de +Z para +X, que é a ordem invertida da convenção de atan2.
      const { facingX, facingZ } = player;
      if (facingX !== undefined && facingZ !== undefined) {
        mesh.rotation.y = Math.atan2(facingX, facingZ) + tokens.world.avatarYaw;
      }
    }

    for (const [id, mesh] of this.meshes) {
      if (this.isPresent(players, id)) continue;
      this.scene.remove(mesh);
      this.meshes.delete(id);
    }
    for (const [id, instance] of this.rigs) {
      if (this.isPresent(players, id)) continue;
      this.scene.remove(instance.object);
      instance.dispose();
      this.rigs.delete(id);
      this.lastPosition.delete(id);
      this.speeds.delete(id);
    }
  }

  private isPresent(players: readonly PlayerView[], id: number): boolean {
    for (const player of players) {
      if (player.id === id) return true;
    }
    return false;
  }

  private rigOf(id: number): AvatarInstance | undefined {
    return this.rigs.get(id) ?? this.addRig(id, 0, 0);
  }

  private addRig(id: number, x: number, z: number): AvatarInstance | undefined {
    if (this.rig === undefined) return undefined;
    const instance = this.rig.create();
    instance.object.position.set(x, 0, z);
    this.rigs.set(id, instance);
    // Alocado UMA vez, quando o jogador entra. `animate` só escreve nele.
    this.lastPosition.set(id, new Vector2(x, z));
    this.scene.add(instance.object);
    return instance;
  }

  /** Reconexão devolve outro assento; sem isto o "você" ficaria na cor errada. */
  setSelfId(id: number): void {
    this.selfId = id;
  }

  dispose(): void {
    for (const mesh of this.meshes.values()) this.scene.remove(mesh);
    this.meshes.clear();
    for (const instance of this.rigs.values()) {
      this.scene.remove(instance.object);
      instance.dispose();
    }
    this.rigs.clear();
    this.rig?.dispose();
    this.boxGeometry.dispose();
    this.avatarGeometry?.dispose();
    this.selfMaterial.dispose();
    this.otherMaterial.dispose();
    this.deadMaterial.dispose();
  }

  private materialFor(player: PlayerView): MeshLambertMaterial {
    if (player.hp <= 0) return this.deadMaterial;
    return player.id === this.selfId ? this.selfMaterial : this.otherMaterial;
  }
}
